import type { DatabaseShardManifest } from '@blocksuite/affine-model';

const DATABASE_SYSTEM_DOC_ACCESS_RETRY_DELAYS_MS = [1000, 5000, 15000] as const;
const RETRYABLE_DATABASE_SYSTEM_DOC_ACCESS_STATUS_CODES = new Set([
  408, 429, 500, 502, 503, 504,
]);

export type DatabaseSystemDocAccessRegistrationPayload = {
  workspaceId: string;
  databaseId: string;
  rootDocId: string;
  accessDocId: string;
  blockId: string;
  manifest: DatabaseShardManifest;
};

type PendingDatabaseSystemDocAccessRegistration = {
  state: 'inflight' | 'retryScheduled';
  timer?: ReturnType<typeof setTimeout>;
  signature: string;
  payload: DatabaseSystemDocAccessRegistrationPayload;
  attempt: number;
};

type RegisterDatabaseSystemDocAccessBindingOptions = {
  attempt?: number;
  clearTimeoutImpl?: typeof clearTimeout;
  fetchImpl: typeof fetch;
  isDisposed?: () => boolean;
  queueMicrotaskImpl?: typeof queueMicrotask;
  setTimeoutImpl?: typeof setTimeout;
  warn?: typeof console.warn;
};

const registeredDatabaseSystemDocAccessSignatures = new Set<string>();
const pendingDatabaseSystemDocAccessRegistrations = new Map<
  string,
  PendingDatabaseSystemDocAccessRegistration
>();

class DatabaseSystemDocAccessRegistrationHttpError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${status}`);
    this.name = 'DatabaseSystemDocAccessRegistrationHttpError';
  }
}

export function cloneDatabaseShardManifest(
  manifest: DatabaseShardManifest
): DatabaseShardManifest {
  return {
    ...manifest,
    shards: manifest.shards.map(shard => ({ ...shard })),
  };
}

function createDatabaseSystemDocAccessRegistrationKey(
  payload: DatabaseSystemDocAccessRegistrationPayload
): string {
  const { workspaceId, databaseId, rootDocId, accessDocId, blockId } = payload;
  return JSON.stringify({
    workspaceId,
    databaseId,
    rootDocId,
    accessDocId,
    blockId,
  });
}

function createDatabaseSystemDocAccessRegistrationSignature(
  payload: DatabaseSystemDocAccessRegistrationPayload
): string {
  return JSON.stringify({
    workspaceId: payload.workspaceId,
    databaseId: payload.databaseId,
    rootDocId: payload.rootDocId,
    accessDocId: payload.accessDocId,
    blockId: payload.blockId,
    shards: payload.manifest.shards.map(shard => ({
      shardId: shard.shardId,
      serverDocId: shard.serverDocId,
    })),
  });
}

function isRetryableDatabaseSystemDocAccessRegistrationError(
  error: unknown
): boolean {
  if (error instanceof DatabaseSystemDocAccessRegistrationHttpError) {
    return RETRYABLE_DATABASE_SYSTEM_DOC_ACCESS_STATUS_CODES.has(error.status);
  }
  return true;
}

export function resetDatabaseSystemDocAccessRegistrationStateForTest(): void {
  registeredDatabaseSystemDocAccessSignatures.clear();
  for (const pending of pendingDatabaseSystemDocAccessRegistrations.values()) {
    if (pending.timer) {
      clearTimeout(pending.timer);
    }
  }
  pendingDatabaseSystemDocAccessRegistrations.clear();
}

export function registerDatabaseSystemDocAccessBindingRequest(
  payload: DatabaseSystemDocAccessRegistrationPayload,
  {
    attempt = 0,
    clearTimeoutImpl = clearTimeout,
    fetchImpl,
    isDisposed = () => false,
    queueMicrotaskImpl = queueMicrotask,
    setTimeoutImpl = setTimeout,
    warn = console.warn,
  }: RegisterDatabaseSystemDocAccessBindingOptions
): void {
  const key = createDatabaseSystemDocAccessRegistrationKey(payload);
  const signature = createDatabaseSystemDocAccessRegistrationSignature(payload);
  if (registeredDatabaseSystemDocAccessSignatures.has(signature)) return;

  const pending = pendingDatabaseSystemDocAccessRegistrations.get(key);
  if (pending?.signature === signature) {
    return;
  }
  if (pending?.timer) {
    clearTimeoutImpl(pending.timer);
  }

  const startAttempt = (currentAttempt: number) => {
    const latest = pendingDatabaseSystemDocAccessRegistrations.get(key);
    if (!latest || latest.signature !== signature) return;
    if (typeof fetchImpl !== 'function') return;
    if (registeredDatabaseSystemDocAccessSignatures.has(signature)) {
      pendingDatabaseSystemDocAccessRegistrations.delete(key);
      return;
    }
    pendingDatabaseSystemDocAccessRegistrations.set(key, {
      state: 'inflight',
      signature,
      payload,
      attempt: currentAttempt,
    });

    void fetchImpl(
      `/api/workspace/${encodeURIComponent(payload.workspaceId)}/database-imports/system-doc-access`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bindings: [
            {
              databaseId: payload.databaseId,
              rootDocId: payload.rootDocId,
              accessDocId: payload.accessDocId,
              blockId: payload.blockId,
              manifest: payload.manifest,
            },
          ],
        }),
      }
    )
      .then(response => {
        if (!response.ok) {
          throw new DatabaseSystemDocAccessRegistrationHttpError(
            response.status
          );
        }
        registeredDatabaseSystemDocAccessSignatures.add(signature);
        const latest = pendingDatabaseSystemDocAccessRegistrations.get(key);
        if (latest?.signature === signature) {
          pendingDatabaseSystemDocAccessRegistrations.delete(key);
        }
      })
      .catch(error => {
        const latest = pendingDatabaseSystemDocAccessRegistrations.get(key);
        if (latest?.signature !== signature) {
          return;
        }
        const retryable =
          isRetryableDatabaseSystemDocAccessRegistrationError(error);
        const shouldRetry =
          retryable &&
          !isDisposed() &&
          currentAttempt < DATABASE_SYSTEM_DOC_ACCESS_RETRY_DELAYS_MS.length;
        if (!retryable || !shouldRetry) {
          warn(
            `Failed to register database system document "${payload.rootDocId}".`,
            error
          );
        }
        if (shouldRetry) {
          scheduleAttempt(currentAttempt + 1);
          return;
        }
        pendingDatabaseSystemDocAccessRegistrations.delete(key);
      });
  };

  const scheduleAttempt = (nextAttempt: number) => {
    const retryAttempt = Math.max(0, nextAttempt - 1);
    const retryDelayMs =
      DATABASE_SYSTEM_DOC_ACCESS_RETRY_DELAYS_MS[
        Math.min(
          retryAttempt,
          DATABASE_SYSTEM_DOC_ACCESS_RETRY_DELAYS_MS.length - 1
        )
      ];
    const timer = setTimeoutImpl(() => startAttempt(nextAttempt), retryDelayMs);
    pendingDatabaseSystemDocAccessRegistrations.set(key, {
      state: 'retryScheduled',
      timer,
      signature,
      payload,
      attempt: nextAttempt,
    });
  };

  pendingDatabaseSystemDocAccessRegistrations.set(key, {
    state: 'retryScheduled',
    signature,
    payload,
    attempt,
  });

  if (attempt === 0) {
    queueMicrotaskImpl(() => startAttempt(0));
  } else {
    scheduleAttempt(attempt);
  }
}
